import React, { useEffect, useMemo, useState } from 'react'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import AdmiSubHeader from '../../components/subheader/admi-subheader.components'
import useAuth, { useOrder } from '../../hooks/useAuth'
import {
  errorToast,
  imageDb,
  infoToast,
  resetPassword,
} from '../../utils/firebase/firebase.utils'
import { AdmiMenu } from './admi-menu.components'
import './admi.styles.scss'
import './settings.styles.scss'

const defaultHours = () => ({
  summary: '',
  detail: '',
})

const defaultCompany = () => ({
  companyDisplayName: '',
  companyName: '',
  slogan: '',
  address1: '',
  address2: '',
  phone: '',
  email: '',
  country: '',
  ig: '',
  fb: '',
  tw: '',
  tq: '',
  yt: '',
  logo: '',
  createdAt: new Date(),
})

const nextFileId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const SettingsEdit = () => {
  const { currentUser, users, getUsers, addStaffUser, updateStaffUser } = useAuth()
  const { curPage, addPage, getCurPage } = useOrder()

  const [hours, setHours] = useState(defaultHours)
  const [company, setCompany] = useState(defaultCompany)
  const [savingHours, setSavingHours] = useState(false)
  const [savingCompany, setSavingCompany] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [staffForm, setStaffForm] = useState({
    displayName: '',
    email: '',
    password: '',
    asAdmin: true,
  })
  const [addingStaff, setAddingStaff] = useState(false)
  const [busyUid, setBusyUid] = useState('')
  const [refreshingStaff, setRefreshingStaff] = useState(false)

  useEffect(() => {
    if (typeof getCurPage === 'function') getCurPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!currentUser || typeof getUsers !== 'function') return undefined

    let cancelled = false
    getUsers()
      .then(() => {
        if (cancelled) return
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Settings staff load failed:', error)
      })

    return () => {
      cancelled = true
    }
  }, [currentUser, getUsers])

  useEffect(() => {
    if (!curPage) return

    const nextHours =
      curPage.hours && typeof curPage.hours === 'object' && !Array.isArray(curPage.hours)
        ? { ...defaultHours(), ...curPage.hours }
        : defaultHours()
    setHours(nextHours)

    const nextCompany =
      curPage.company && !Array.isArray(curPage.company)
        ? { ...defaultCompany(), ...curPage.company }
        : defaultCompany()
    setCompany(nextCompany)
  }, [curPage])

  const staffList = useMemo(() => {
    const list = Array.isArray(users) ? users : []
    return [...list].sort((a, b) => {
      const aDel = a.del === 'yes' ? 1 : 0
      const bDel = b.del === 'yes' ? 1 : 0
      if (aDel !== bDel) return aDel - bDel
      return String(a.email || '').localeCompare(String(b.email || ''))
    })
  }, [users])

  const setCompanyField = (name, value) => {
    setCompany((prev) => ({ ...prev, [name]: value }))
  }

  const buildPagePayload = (overrides = {}) => {
    const { id: _ignoredId, ...base } = curPage || {}
    return {
      ...base,
      company,
      hours: {
        summary: (hours.summary || '').trim(),
        detail: (hours.detail || '').trim(),
      },
      del: 'no',
      ...overrides,
    }
  }

  const saveHours = async () => {
    if (!curPage) {
      return infoToast('Save homepage content in Pages once, then return here for hours.')
    }

    const ok = window.confirm('Save business hours?')
    if (!ok) return

    setSavingHours(true)
    try {
      await addPage(
        buildPagePayload({
          hours: {
            summary: (hours.summary || '').trim(),
            detail: (hours.detail || '').trim(),
          },
        }),
        'Business hours updated.'
      )
    } catch (error) {
      console.error('Save hours failed:', error)
      errorToast('Could not save hours. Please try again.')
    } finally {
      setSavingHours(false)
    }
  }

  const saveCompany = async () => {
    if (!curPage) {
      return infoToast('Save homepage content in Pages once, then return here for company details.')
    }

    const ok = window.confirm('Save company details?')
    if (!ok) return

    setSavingCompany(true)
    try {
      await addPage(
        buildPagePayload({
          company: {
            ...company,
            companyDisplayName: (company.companyDisplayName || '').trim(),
            companyName: (company.companyName || '').trim(),
            slogan: (company.slogan || '').trim(),
            email: (company.email || '').trim(),
            phone: (company.phone || '').trim(),
            country: (company.country || '').trim(),
            address1: (company.address1 || '').trim(),
            address2: (company.address2 || '').trim(),
            ig: (company.ig || '').trim(),
            fb: (company.fb || '').trim(),
          },
        }),
        'Company details updated.'
      )
    } catch (error) {
      console.error('Save company failed:', error)
      errorToast('Could not save company details. Please try again.')
    } finally {
      setSavingCompany(false)
    }
  }

  const uploadLogo = async (file) => {
    if (!file) return
    setUploadingLogo(true)
    try {
      const imgRef = ref(imageDb, `files/${nextFileId()}-${file.name}`)
      const snapshot = await uploadBytes(imgRef, file)
      const url = await getDownloadURL(snapshot.ref)
      setCompanyField('logo', url)
      infoToast('Logo uploaded. Click Save company to publish it.')
    } catch (error) {
      console.error('Logo upload failed:', error)
      errorToast('Could not upload logo. Please try again.')
    } finally {
      setUploadingLogo(false)
    }
  }

  const createStaff = async (event) => {
    event.preventDefault()
    const email = staffForm.email.trim()
    const password = staffForm.password
    const displayName = staffForm.displayName.trim()

    if (!email || !password) {
      return infoToast('Email and password are required.')
    }
    if (password.length < 6) {
      return infoToast('Password must be at least 6 characters.')
    }

    setAddingStaff(true)
    try {
      await addStaffUser(
        {
          email,
          password,
          displayName,
          asAdmin: Boolean(staffForm.asAdmin),
        },
        staffForm.asAdmin ? 'Admin staff user created.' : 'Staff user created.'
      )
      setStaffForm({
        displayName: '',
        email: '',
        password: '',
        asAdmin: true,
      })
    } catch (error) {
      console.error('Create staff failed:', error)
      const code = error?.code || ''
      if (code === 'auth/email-already-in-use') {
        errorToast('That email already has an account.')
      } else if (code === 'auth/invalid-email') {
        errorToast('Enter a valid email address.')
      } else if (code === 'auth/weak-password') {
        errorToast('Choose a stronger password (6+ characters).')
      } else {
        errorToast('Could not create staff user. Please try again.')
      }
    } finally {
      setAddingStaff(false)
    }
  }

  const setStaffStatus = async (user, nextStatus) => {
    if (!user?.uid) return
    if (user.uid === currentUser?.uid && nextStatus !== 'admin') {
      return infoToast('You cannot remove admin from your own account.')
    }

    const ok = window.confirm(
      nextStatus === 'admin'
        ? `Make ${user.email || 'this user'} an admin?`
        : `Remove admin access for ${user.email || 'this user'}?`
    )
    if (!ok) return

    setBusyUid(user.uid)
    try {
      await updateStaffUser(
        {
          uid: user.uid,
          status: nextStatus,
          del: user.del === 'yes' ? 'no' : user.del || 'no',
        },
        nextStatus === 'admin' ? 'Admin access granted.' : 'Admin access removed.'
      )
    } catch (error) {
      console.error('Update staff status failed:', error)
      errorToast('Could not update staff access.')
    } finally {
      setBusyUid('')
    }
  }

  const setStaffDeleted = async (user, deleted) => {
    if (!user?.uid) return
    if (user.uid === currentUser?.uid) {
      return infoToast('You cannot disable your own account.')
    }

    const ok = window.confirm(
      deleted
        ? `Disable login for ${user.email || 'this user'}?`
        : `Re-enable login for ${user.email || 'this user'}?`
    )
    if (!ok) return

    setBusyUid(user.uid)
    try {
      await updateStaffUser(
        {
          uid: user.uid,
          del: deleted ? 'yes' : 'no',
          status: user.status || 'user',
        },
        deleted ? 'Staff login disabled.' : 'Staff login re-enabled.'
      )
    } catch (error) {
      console.error('Update staff active state failed:', error)
      errorToast('Could not update staff user.')
    } finally {
      setBusyUid('')
    }
  }

  const refreshStaffList = async () => {
    if (refreshingStaff) return
    if (typeof getUsers !== 'function') {
      return infoToast('Staff list is not available right now.')
    }
    if (!currentUser || currentUser.status !== 'admin') {
      return errorToast('Admin access required to load staff users.')
    }

    setRefreshingStaff(true)
    try {
      const list = await getUsers()
      infoToast(
        `Staff list refreshed${Array.isArray(list) ? ` (${list.length})` : ''}.`
      )
    } catch (error) {
      console.error('Refresh staff failed:', error)
      errorToast(error?.message || 'Could not refresh staff list.')
    } finally {
      setRefreshingStaff(false)
    }
  }

  const sendStaffPasswordReset = async (user) => {
    const email = (user?.email || '').trim()
    if (!email) {
      return infoToast('This user has no email address.')
    }
    if (user.del === 'yes') {
      return infoToast('Re-enable this user before sending a reset email.')
    }

    const ok = window.confirm(`Send a password reset email to ${email}?`)
    if (!ok) return

    const uid = user.uid || user.id || email
    setBusyUid(uid)
    try {
      await resetPassword(email)
      infoToast(`Password reset email sent to ${email}.`)
    } catch (error) {
      console.error('Password reset failed:', error)
      const code = error?.code || ''
      if (code === 'auth/user-not-found') {
        errorToast('No Auth account found for that email.')
      } else if (code === 'auth/invalid-email') {
        errorToast('That email address is invalid.')
      } else if (code === 'auth/too-many-requests') {
        errorToast('Too many reset attempts. Try again later.')
      } else {
        errorToast('Could not send reset email. Please try again.')
      }
    } finally {
      setBusyUid('')
    }
  }

  return (
    <div className="admin-shell">
      <AdmiSubHeader h2="Settings" h6="Company, hours, and Staff/Users" />
      <AdmiMenu />

      <div className="admin-shell__body settings-v2">
        <section className="settings-v2__panel">
          <h3>Signed in</h3>
          <p className="settings-v2__lead">Current staff session for this browser.</p>
          <dl className="settings-v2__facts">
            <div>
              <dt>Email</dt>
              <dd>{currentUser?.email || 'Unknown'}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{currentUser?.status === 'admin' ? 'Admin' : currentUser?.status || 'Staff'}</dd>
            </div>
          </dl>
        </section>

        <section className="settings-v2__panel">
          <div className="settings-v2__head">
            <div>
              <h3>Company setup</h3>
              <p className="settings-v2__lead">
                Public contact details used on the site, contact form, and legal pages.
              </p>
            </div>
          </div>

          <div className="settings-v2__grid">
            <label className="settings-v2__field">
              <span>Display name</span>
              <input
                type="text"
                maxLength={24}
                value={company.companyDisplayName || ''}
                onChange={(event) => setCompanyField('companyDisplayName', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Legal / full name</span>
              <input
                type="text"
                value={company.companyName || ''}
                onChange={(event) => setCompanyField('companyName', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Slogan</span>
              <input
                type="text"
                value={company.slogan || ''}
                onChange={(event) => setCompanyField('slogan', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Email</span>
              <input
                type="email"
                value={company.email || ''}
                onChange={(event) => setCompanyField('email', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Phone</span>
              <input
                type="text"
                maxLength={24}
                value={company.phone || ''}
                onChange={(event) => setCompanyField('phone', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Country</span>
              <input
                type="text"
                value={company.country || ''}
                onChange={(event) => setCompanyField('country', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Address line 1</span>
              <input
                type="text"
                value={company.address1 || ''}
                onChange={(event) => setCompanyField('address1', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Address line 2</span>
              <input
                type="text"
                value={company.address2 || ''}
                onChange={(event) => setCompanyField('address2', event.target.value)}
              />
            </label>
            <label className="settings-v2__field">
              <span>Instagram URL</span>
              <input
                type="url"
                value={company.ig || ''}
                onChange={(event) => setCompanyField('ig', event.target.value)}
                placeholder="https://instagram.com/…"
              />
            </label>
            <label className="settings-v2__field">
              <span>Facebook URL</span>
              <input
                type="url"
                value={company.fb || ''}
                onChange={(event) => setCompanyField('fb', event.target.value)}
                placeholder="https://facebook.com/…"
              />
            </label>
          </div>

          <div className="settings-v2__logo">
            <span className="settings-v2__field-label">Logo</span>
            <div className="settings-v2__logo-row">
              <div className="settings-v2__logo-preview">
                {company.logo ? <img src={company.logo} alt="" /> : <span>No logo</span>}
              </div>
              <div className="settings-v2__logo-controls">
                <input
                  type="url"
                  value={company.logo || ''}
                  onChange={(event) => setCompanyField('logo', event.target.value)}
                  placeholder="https://… or upload a file"
                />
                <label className="settings-v2__upload">
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingLogo || savingCompany}
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      if (file) uploadLogo(file)
                    }}
                  />
                  {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                </label>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="settings-v2__save"
            disabled={savingCompany || uploadingLogo}
            onClick={saveCompany}
          >
            {savingCompany ? 'Saving…' : 'Save company'}
          </button>
        </section>

        <section className="settings-v2__panel">
          <div className="settings-v2__head">
            <div>
              <h3>Business hours</h3>
              <p className="settings-v2__lead">
                Shown in the site footer when a summary is saved.
              </p>
            </div>
          </div>

          <label className="settings-v2__field">
            <span>Hours summary</span>
            <input
              type="text"
              value={hours.summary}
              onChange={(event) =>
                setHours((prev) => ({ ...prev, summary: event.target.value }))
              }
              placeholder="Tue–Sat 10am–6pm · Sun by appointment"
              maxLength={90}
            />
          </label>

          <label className="settings-v2__field">
            <span>Extra note</span>
            <textarea
              rows={3}
              value={hours.detail}
              onChange={(event) =>
                setHours((prev) => ({ ...prev, detail: event.target.value }))
              }
              placeholder="Closed Mondays. Custom cakes need a few days’ notice."
              maxLength={220}
            />
          </label>

          <button
            type="button"
            className="settings-v2__save"
            disabled={savingHours}
            onClick={saveHours}
          >
            {savingHours ? 'Saving…' : 'Save hours'}
          </button>
        </section>

        <section className="settings-v2__panel">
          <div className="settings-v2__head">
            <div>
              <h3>Staff/Users</h3>
              <p className="settings-v2__lead">
                Create login accounts and control who can open Manage.
              </p>
            </div>
          </div>

          <form className="settings-v2__staff-form" onSubmit={createStaff}>
            <div className="settings-v2__grid">
              <label className="settings-v2__field">
                <span>Display name</span>
                <input
                  type="text"
                  value={staffForm.displayName}
                  onChange={(event) =>
                    setStaffForm((prev) => ({ ...prev, displayName: event.target.value }))
                  }
                  placeholder="Sandy"
                />
              </label>
              <label className="settings-v2__field">
                <span>Email</span>
                <input
                  type="email"
                  required
                  value={staffForm.email}
                  onChange={(event) =>
                    setStaffForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  placeholder="staff@example.com"
                  autoComplete="off"
                />
              </label>
              <label className="settings-v2__field">
                <span>Temp password</span>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={staffForm.password}
                  onChange={(event) =>
                    setStaffForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                />
              </label>
            </div>

            <div className="settings-v2__staff-form-footer">
              <label className="settings-v2__check">
                <input
                  type="checkbox"
                  checked={staffForm.asAdmin}
                  onChange={(event) =>
                    setStaffForm((prev) => ({ ...prev, asAdmin: event.target.checked }))
                  }
                />
                <span>Grant admin access immediately</span>
              </label>

              <button type="submit" className="settings-v2__save" disabled={addingStaff}>
                {addingStaff ? 'Creating…' : 'Add user'}
              </button>
            </div>
          </form>

          <div className="settings-v2__staff-list-head">
            <h4>All users</h4>
            <button
              type="button"
              className={`settings-v2__ghost${refreshingStaff ? ' is-busy' : ''}`}
              onClick={refreshStaffList}
              disabled={refreshingStaff}
            >
              {refreshingStaff ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {staffList.length === 0 ? (
            <p className="settings-v2__empty">No users found yet.</p>
          ) : (
            <ul className="settings-v2__staff-list">
              {staffList.map((user) => {
                const uid = user.uid || user.id
                const isYou = uid === currentUser?.uid
                const isAdmin = user.status === 'admin'
                const disabled = user.del === 'yes'
                const busy = busyUid === uid

                return (
                  <li key={uid || user.email} className={disabled ? 'is-disabled' : ''}>
                    <div>
                      <p className="settings-v2__staff-name">
                        {user.displayName || 'Staff'}
                        {isYou ? <em>you</em> : null}
                      </p>
                      <p className="settings-v2__staff-email">{user.email || 'No email'}</p>
                      <p className="settings-v2__staff-badges">
                        <span className={isAdmin ? 'is-admin' : ''}>
                          {isAdmin ? 'Admin' : 'User'}
                        </span>
                        {disabled ? <span className="is-off">Disabled</span> : null}
                      </p>
                    </div>
                    <div className="settings-v2__staff-actions">
                      <button
                        type="button"
                        disabled={busy || disabled || !user.email}
                        onClick={() => sendStaffPasswordReset(user)}
                      >
                        {busy ? 'Working…' : 'Reset password'}
                      </button>
                      {isAdmin ? (
                        <button
                          type="button"
                          disabled={busy || isYou}
                          onClick={() => setStaffStatus(user, 'user')}
                        >
                          Remove admin
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy || disabled}
                          onClick={() => setStaffStatus(user, 'admin')}
                        >
                          Make admin
                        </button>
                      )}
                      {disabled ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setStaffDeleted(user, false)}
                        >
                          Re-enable
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="is-danger"
                          disabled={busy || isYou}
                          onClick={() => setStaffDeleted(user, true)}
                        >
                          Disable
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

export default SettingsEdit
